import { AxiosAdapter, AxiosResponse, InternalAxiosRequestConfig } from 'axios'
import { Capacitor, CapacitorHttp, HttpResponse } from '@capacitor/core'

const isNativePlatform = Capacitor.isNativePlatform()

function buildQueryString(params: unknown): string {
  if (!params || typeof params !== 'object') {
    return ''
  }

  const searchParams = new URLSearchParams()

  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      if (Array.isArray(value)) {
        // Append each array item separately: key=val1&key=val2
        value.forEach(v => searchParams.append(key, String(v)))
      } else if (typeof value === 'object') {
        searchParams.append(key, JSON.stringify(value))
      } else {
        searchParams.append(key, String(value))
      }
    }
  })

  return searchParams.toString()
}

function isFormData(data: unknown): boolean {
  return typeof FormData !== 'undefined' && data instanceof FormData
}

export const capacitorHttpAdapter: AxiosAdapter = async (
  config: InternalAxiosRequestConfig
): Promise<AxiosResponse> => {
  if (!isNativePlatform) {
    throw new Error('capacitorHttpAdapter should only be used on native platforms')
  }

  if (isFormData(config.data)) {
    const fetchHeaders: Record<string, string> = {}
    if (config.headers) {
      Object.entries(config.headers).forEach(([key, value]) => {
        if (key.toLowerCase() !== 'content-type' && value !== undefined && value !== null) {
          fetchHeaders[key] = String(value)
        }
      })
    }

    const fetchUrl = config.baseURL ? `${config.baseURL}${config.url}` : config.url || ''
    const response = await fetch(fetchUrl, {
      method: config.method?.toUpperCase() || 'POST',
      body: config.data,
      headers: fetchHeaders
    })
    const data = await response.json()
    return {
      data,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries()),
      config,
      request: null
    }
  }

  let url = config.baseURL ? `${config.baseURL}${config.url}` : config.url || ''

  const headers: Record<string, string> = {}
  if (config.headers) {
    Object.entries(config.headers).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        headers[key] = String(value)
      }
    })
  }

  if (config.params) {
    const queryString = buildQueryString(config.params)
    if (queryString) {
      url += (url.includes('?') ? '&' : '?') + queryString
    }
  }

  const options: Parameters<typeof CapacitorHttp.request>[0] = {
    url,
    method: (config.method?.toUpperCase() || 'GET') as 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    headers,
    data: config.data,
    responseType: config.responseType === 'arraybuffer' ? 'blob' : 'json'
  }

  const response: HttpResponse = await CapacitorHttp.request(options)

  return {
    data: response.data,
    status: response.status,
    statusText: '',
    headers: response.headers,
    config,
    request: null
  }
}
